import getConfig from 'next/config';
import {
  getCookie,
  getCookieValue,
  setCookie,
} from '../../util/cookies';

const {
  publicRuntimeConfig: {
    GF_DOTCOM_ENV,
    GF_DOTCOM_SHOPIFY_URL,
  },
} = getConfig();

const OWN_REFERRAL_COOKIE_NAME = 'own_referral';

const getCurrentUserAgent = () =>
  (typeof navigator === 'undefined' ? '' : navigator.userAgent || '');

const hashUserAgent = (userAgent = '') => {
  let hash = 5381;

  for (let index = 0; index < userAgent.length; index += 1) {
    hash = ((hash * 33) + userAgent.charCodeAt(index)) % 4294967291;
  }

  return Math.abs(hash).toString(16);
};

export const parseAffiliate = (affiliate) => {
  const values = affiliate.trim().split('-');

  return {
    name: values[0] || null,
    code: values[1] || null,
    params: values.slice(2).join('|') || null,
  };
};

const parseAffiliateCookie = (cookie) => ({
  name: getCookieValue(cookie, 'name|', 'code|'),
  code: getCookieValue(cookie, 'code|', 'params|'),
  params: getCookieValue(cookie, 'params|'),
});

export const parseAffiliateURL = () => {
  if (typeof window === 'undefined') return null;

  const campaign = new URLSearchParams(window.location.search).get('utm_campaign') || '';
  const source = new URLSearchParams(window.location.search).get('utm_source') || '';

  if (!campaign || !source || source !== 'impact') return null;

  return parseAffiliate(decodeURI(campaign));
};

export const getAffiliateInfo = () => {
  const existingAffiliate = getCookie('affiliate');
  let affiliateInfo;

  if (existingAffiliate) {
    affiliateInfo = {
      ...parseAffiliateCookie(existingAffiliate),
      hasCookie: true,
    };
  } else {
    affiliateInfo = parseAffiliateURL();

    if (affiliateInfo?.params?.includes('content_partner')) return undefined;
  }

  return affiliateInfo;
};

export const buildShopifyPrinterUrl = () => {
  const collection = GF_DOTCOM_ENV === 'production' ? 'printers' : 'glowforge-printers';
  return `${GF_DOTCOM_SHOPIFY_URL}/collections/${collection}`;
};

export const parseReferralCookie = (cookie) => ({
  code: getCookieValue(cookie, 'code|', 'fbuy|'),
  name: getCookieValue(cookie, 'name|', 'code|'),
  shopUrl: buildShopifyPrinterUrl(),
  parameters: {
    fbuy: getCookieValue(cookie, 'fbuy|', 'fbshare|'),
    fbshare: getCookieValue(cookie, 'fbshare|', 'referrerId|'),
    referrerId: getCookieValue(cookie, 'referrerId|'),
  },
});

export const buildReferralInfo = (referrer = {}) => {
  const code = referrer.referralCode || referrer.code || '';

  return {
    name: referrer.name || '',
    code,
    shopUrl: buildShopifyPrinterUrl(),
  };
};

export const buildReferralCookieValue = (referrerName = '', referralCode = '') =>
  `name|${referrerName}code|${referralCode}fbuy|fbshare|referrerId|`;

export const buildOwnReferralCookieValue = (
  referralCode = '',
  userAgent = getCurrentUserAgent()
) =>
  `code|${referralCode}ua|${hashUserAgent(userAgent)}setAt|${Date.now()}`;

export const parseOwnReferralCookie = (cookie) => ({
  code: getCookieValue(cookie, 'code|', 'ua|'),
  userAgentHash: getCookieValue(cookie, 'ua|', 'setAt|'),
  setAt: Number.parseInt(getCookieValue(cookie, 'setAt|'), 10) || null,
});

export const setReferralCookie = (referrerName, referralCode) => {
  if (!referralCode) return;

  setCookie(
    'referral',
    buildReferralCookieValue(referrerName, referralCode),
    30,
    false,
  );
};

export const setOwnReferralCookie = (
  referralCode,
  userAgent = getCurrentUserAgent()
) => {
  if (!referralCode) return;

  setCookie(
    OWN_REFERRAL_COOKIE_NAME,
    buildOwnReferralCookieValue(referralCode, userAgent),
    30,
    false,
  );
};

export const isSameBrowserOwnReferral = (
  incomingReferralCode,
  ownReferralCookie = getCookie(OWN_REFERRAL_COOKIE_NAME),
  userAgent = getCurrentUserAgent()
) => {
  if (!incomingReferralCode || !ownReferralCookie?.includes('code|')) {
    return false;
  }

  const { code, userAgentHash } = parseOwnReferralCookie(ownReferralCookie);
  if (!code || code !== incomingReferralCode) {
    return false;
  }

  if (!userAgentHash) {
    return true;
  }

  return userAgentHash === hashUserAgent(userAgent);
};

export const setAffiliateCookie = (affiliateInfo) => {
  if (!affiliateInfo?.code) return;

  setCookie(
    'affiliate',
    `name|${affiliateInfo.name}code|${affiliateInfo.code}params|${affiliateInfo.params}`,
    30,
    false,
  );
};

export const getReferralShareOrigin = () =>
  (GF_DOTCOM_ENV === 'production'
    ? 'https://share.glowforge.com'
    : 'https://share-staging.glowforge.com');

export const buildReferralShareUrl = (referralCode) =>
  `${getReferralShareOrigin()}/${referralCode}`;
